import { Col, Layout, Row, Space, Typography, message } from "antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import RevoCard from "../../../assets/images/revo_card.webp";
import ArrowLeftIconComponent from "../../../components/ArrowLeftIconComponent";
import ChangePaymentModalComponent from "../../../components/ChangePaymentModalComponent";
import CreatorDeletePaymentMethodModal from "../../../components/CreatorDeletePaymentMethodModal";
import CreatorSwitchBankAccountModalComponent from "../../../components/CreatorSwitchBankAccountModalComponent";
import SpinnerComponent from "../../../components/SpinnerComponent/SpinnerComponent";
import StickyButtonComponent from "../../../components/StickyButtonComponent";
import { IUserBanks } from "../../../helpers/types";
import useFetchUserBanks from "../../../hooks/userHooks/useFetchUserBanks";
import useFetchUserInfos from "../../../hooks/userHooks/useFetchUserInfos";
import { AppDispatch } from "../../../redux/store";
import { removeBankAccount, updateDefaultBankAccount, updatePayoutFrequency } from "../../../redux/users/usersSlice";
import { userService } from "../../../services";
import "./index.less";

const CreatorPaymentMethodPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { currentUser: user } = useFetchUserInfos(true);
  const { banks } = useFetchUserBanks();
  const [isSwitchAccountModalOpen, setIsSwitchAccountModalOpen] = useState<boolean>(false);
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState<boolean>(false);
  const [accountSelected, setAccountSelected] = useState<any>({});
  const [changePaymentIsLoading, setChangePaymentIsLoading] = useState<boolean>(false);
  const [changePaymentModalIsOpen, setChangePaymentModalIsOpen] = useState<boolean>(false);
  const [temporarySelectedMethod, setTemporarySelectedMethod] = useState<number>(0);
  const payoutStatus = user?.infos?.privateInfos?.payout_status || false;
  const paymentFrequency = user?.infos?.privateInfos?.paymentFrequency || 0;

  const handleUpdateSelectedMethod = (newPaymentFrequency: number = 0) => {
    setChangePaymentIsLoading(true);
    userService
      .updatePayoutFrequency({
        payout_status: [30, 7].includes(newPaymentFrequency),
        paymentFrequency: newPaymentFrequency || paymentFrequency,
      })
      .then((result) => {
        dispatch(updatePayoutFrequency(result));
      })
      .catch((error: any) => {
        message.error(error || "Something went wrong.");
      })
      .finally(() => {
        setChangePaymentIsLoading(false);
      });
  };

  const switchAccountByDefault = () => {
    userService.setDefaultBankAccount(accountSelected._id).then((result) => {
      if (result.changed) {
        dispatch(updateDefaultBankAccount(accountSelected._id));
        setIsSwitchAccountModalOpen(false);
      }
    });
  };

  const deleteBankAccount = async () => {
    userService.deleteBankAccount(accountSelected._id).then((result) => {
      if (result.deleted) {
        dispatch(removeBankAccount(accountSelected._id));
        setIsDeleteAccountModalOpen(false);
        setIsSwitchAccountModalOpen(false);
      }
    });
  };

  const handleSelectMethod = (method: number = 0) => {
    setChangePaymentModalIsOpen(true);
    setTemporarySelectedMethod(method);
  };

  const confirmChangePaymentMethod = () => {
    handleUpdateSelectedMethod(temporarySelectedMethod);
    setChangePaymentModalIsOpen(false);
  };

  return (
    <Layout>
      <Layout.Content className="content creatorPaymentMethodContentWrapper gradient-background-blue-settings">
        <Row className="relative justify-content-center header-title-container">
          <ArrowLeftIconComponent left={0} top={1} />
          <Typography.Text className="header-title-black-24 ml-10">{t("payment-method")}</Typography.Text>
        </Row>
        <Row className="w-full mt-50">
          <Typography.Text className="font-32-bold text-white-color">{t("whenever-you-want")}</Typography.Text>
        </Row>
        {changePaymentIsLoading ? (
          <Row className="change-payment-loading-container">
            <SpinnerComponent />
          </Row>
        ) : (
          <Row className="select-method-block">
            <Col className={`select-method-col left ${paymentFrequency === 7 && payoutStatus ? "active" : ""}`} onClick={() => handleSelectMethod(7)}>
              <div className="select-method-content">
                <Typography.Text className="font-12-regular text-white-color select-method-top">{t("versement")}</Typography.Text>
                <Typography.Text className="font-18-bold text-white-color select-method-center">{t("every-seven-days")}</Typography.Text>
              </div>
            </Col>
            <div className="select-method-divider" />
            <Col className={`select-method-col ${paymentFrequency === 30 && payoutStatus ? "active" : ""}`} onClick={() => handleSelectMethod(30)}>
              <div className="select-method-content">
                <Typography.Text className="font-12-regular text-white-color select-method-top">{t("versement")}</Typography.Text>
                <Typography.Text className="font-18-bold text-white-color select-method-center">{t("every-thirty-days")}</Typography.Text>
              </div>
            </Col>
            <div className="select-method-divider" />
            <Col className={`select-method-col right ${!payoutStatus ? "active" : ""}`} onClick={() => handleSelectMethod()}>
              <div className="select-method-content">
                <Typography.Text className="font-12-regular text-white-color select-method-top">{t("versement")}</Typography.Text>
                <Typography.Text className="font-18-bold text-white-color select-method-center">{t("on-pause")}</Typography.Text>
              </div>
            </Col>
          </Row>
        )}
        <Row className="style-bank">
          <Typography.Text className="font-32-bold text-white-color">{t("bank")}</Typography.Text>
        </Row>
        <Row>
          <Space className="w-full" direction="vertical">
            {banks.map((item: IUserBanks, index: React.Key | null | undefined) => {
              return (
                <Row
                  key={index}
                  onClick={() => {
                    setAccountSelected(item);
                    setIsSwitchAccountModalOpen(true);
                  }}
                >
                  <Row className="w-full justify-content-center">
                    <Row className="image-container">
                      <img src={RevoCard} alt="RevoCard" className="responsive-image" />
                      <Row className="card-overlay">
                        <Row className="card-top-left">
                          <Typography.Text className="title-card text-gradient-color">{t("bank-account")}</Typography.Text>
                        </Row>
                        <Row className="card-top-right">{item.byDefault && <span className="default-option">{t("by-default")}</span>}</Row>
                        <Row className="card-bottom-left">
                          <Typography.Text className="username-card text-gradient-color">
                            {user?.infos.firstName + " " + user?.infos.lastName}
                          </Typography.Text>
                          <Typography.Text className="info-card text-gradient-color">{item.iban}</Typography.Text>
                        </Row>
                      </Row>
                    </Row>
                  </Row>
                </Row>
              );
            })}
          </Space>
        </Row>
        <StickyButtonComponent title={t("add-an-account")} handleClickButton={() => navigate("/creator/newPaymentMethod")} />
        <CreatorSwitchBankAccountModalComponent
          isOpen={isSwitchAccountModalOpen}
          onClose={() => setIsSwitchAccountModalOpen(false)}
          onYesClick={switchAccountByDefault}
          onDelete={() => {
            setIsDeleteAccountModalOpen(true);
            setIsSwitchAccountModalOpen(false);
          }}
        />
        <CreatorDeletePaymentMethodModal
          isOpen={isDeleteAccountModalOpen}
          onClose={() => {
            setIsDeleteAccountModalOpen(false);
            setIsSwitchAccountModalOpen(false);
          }}
          onYesClick={deleteBankAccount}
        />
        <ChangePaymentModalComponent
          isOpen={changePaymentModalIsOpen}
          onClose={() => {
            setChangePaymentModalIsOpen(false);
            setTemporarySelectedMethod(0);
          }}
          changePaymentMethod={confirmChangePaymentMethod}
          selectedMethod={temporarySelectedMethod}
        />
      </Layout.Content>
    </Layout>
  );
};

export default CreatorPaymentMethodPage;
